import { Link } from "react-router-dom";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { BLOG_POSTS } from "@/data/blog";
import { Clock, ArrowUpRight } from "lucide-react";

const BLOG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "Blog pasta-base.cl",
  url: "https://pasta-base.cl/blog",
  description: "Artículos informativos sobre pasta base: prevención, tratamiento, recuperación y familia.",
  blogPost: BLOG_POSTS.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    description: p.description,
    datePublished: p.datePublished,
    url: `https://pasta-base.cl/blog/${p.slug}`,
  })),
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" });
}

export default function Blog() {
  const crumbs = [{ name: "Blog", path: "/blog" }];
  const schemas = [
    BLOG_SCHEMA,
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
  ];

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <>
      <Seo
        title="Blog sobre Pasta Base: Información, Prevención y Recuperación | pasta-base.cl"
        description="Artículos sobre pasta base en Chile: cómo ayudar a un familiar, señales de consumo, tratamiento, recaídas y recuperación. Información verificada y empática."
        path="/blog"
        schema={schemas}
      />

      <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            Artículos · investigación · testimonios
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Blog: Todo sobre la Pasta Base
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-300">
            Guías prácticas para familias, información clínica, testimonios y
            respuestas concretas a las preguntas que más duelen.
          </p>
        </header>

        {/* Featured */}
        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group mt-12 block overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 transition hover:border-emerald-500/40"
          >
            <div className="grid gap-8 p-8 lg:grid-cols-[1.3fr_1fr] lg:p-12">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  Destacado
                </span>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 transition group-hover:text-emerald-300 sm:text-4xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-300">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-slate-400">
                  <span>{formatDate(featured.datePublished)}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {featured.readingTime}
                  </span>
                </div>
              </div>
              <div className="relative flex items-end lg:justify-end">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950 px-5 py-3 text-sm font-semibold text-slate-100 transition group-hover:border-emerald-400 group-hover:text-emerald-300">
                  Leer artículo
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex h-full flex-col rounded-2xl border border-slate-800 bg-slate-900/50 p-6 transition hover:-translate-y-0.5 hover:border-emerald-500/40 hover:bg-slate-900"
            >
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{formatDate(p.datePublished)}</span>
                <span className="h-1 w-1 rounded-full bg-slate-600" />
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {p.readingTime}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-bold leading-tight text-slate-50 transition group-hover:text-emerald-300">
                {p.title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{p.excerpt}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400">
                Leer más
                <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="blog" />
        </div>
      </section>
    </>
  );
}
